import React, {useState, useEffect, useRef} from "react";
import {MultiSelect} from "react-multi-select-component";
import {useTranslation} from "react-i18next";
import {toJS} from "mobx";
import i18next from "i18next";
// local
import {ButtonTransparent} from "../buttons/ButtonTransparent";
import ButtonDelete from "../buttons/ButtonDelete";
import {CustomCounter} from "components/CustomCounter";
import {InitialsBadge} from "components/InitialsBadge";
import {hasPermission} from "helpers/helper";
import {IconPlus, IconClose} from "components/icons/index";
import {color as constColor} from "constants/color.consts";
import cn from "classnames";

// Keep in sync with .dropdown-content max-width in multiselect-custom.scss.
const DROPDOWN_MAX_WIDTH = 300;

const getRightBoundary = (el) => {
  let boundary = window.innerWidth;
  let node = el?.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const {overflow, overflowX} = window.getComputedStyle(node);
    if (
      [overflow, overflowX].some((value) => ["hidden", "auto", "scroll"].includes(value))
    ) {
      boundary = Math.min(boundary, node.getBoundingClientRect().right);
    }
    node = node.parentElement;
  }
  return boundary;
};

const MultipleSelect = ({
  list,
  onMenuToggle,
  onChange,
  disabled,
  disabledTooltip,
  title,
  titleInButton,
  leftLabel,
  uppercaseLabel,
  boldLabel,
  blackTitle,
  tinyTitle,
  uppercaseButton,
  value,
  color,
  small,
  tiny,
  tinyLabel,
  realLabels,
  leftIcon,
  noMultipleChange,
  notShowingSelections,
  noNeedSortingList,
  prefixSelections,
  maxSelectionsInRow,
  customSelectAllElement,
  suffixTitleContent,
  selectAllElement,
  bottomElement,
  highlightIfEmpty,
  needSearch,
  needPermissions,
  initialsSelectionMode,
  currentUserId,
  disableRegions,
  disableCategories,
  requestParam,
  customSuffixSelectedRowcontent,
  addClass,
  addButtonSelectClass,
  searchPlaceholder = "DROPDOWN_SEARCH_PLACEHOLDER",
  alignRight,
}) => {
  const {t} = useTranslation();
  const [selectedValue, setSelectedValue] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [flipToRight, setFlipToRight] = useState(false);
  const addToFiltersRef = useRef();
  const randomNumber = Math.floor(Math.random() * 100);
  const multiSelectClassName = `id-${randomNumber}`;

  const language = i18next.language;

  const shouldAlignRight = !!alignRight && flipToRight;

  const disableMaxSelection =
    requestParam === "availableIn" ? disableRegions : disableCategories;

  const options =
    list &&
    list
      .map(({value, label}) => ({value, label: t(label)}))
      [noNeedSortingList ? "noop" : "sort"]((a, b) =>
        a.label.localeCompare(b.label, language, {
          sensitivity: "base",
        })
      );

  const withCustomSelectAllElement = customSelectAllElement
    ? [{value: customSelectAllElement, label: t(customSelectAllElement)}, ...options]
    : options;

  const finalList = bottomElement
    ? [
        ...withCustomSelectAllElement.filter((item) => item.value !== bottomElement),
        {value: bottomElement, label: t(bottomElement)},
      ]
    : withCustomSelectAllElement;

  const handlerRemove = (value) =>
    realLabels
      ? onChange(selectedValue.filter((x) => x.value !== value))
      : onChange(selectedValue.filter((x) => x.value !== value).map(({value}) => value));

  const openSelect = () => {
    if (needPermissions && !hasPermission(needPermissions)) return;
    if (!isOpen && alignRight && addToFiltersRef.current) {
      const {left} = addToFiltersRef.current.getBoundingClientRect();
      setFlipToRight(
        left + DROPDOWN_MAX_WIDTH > getRightBoundary(addToFiltersRef.current)
      );
    }
    setIsOpen(!isOpen);
  };

  const backdropListener = (e) => {
    if (addToFiltersRef.current && isOpen) {
      const select = addToFiltersRef.current.parentNode.querySelector(
        ".panel-content .select-panel"
      );
      select && !select.contains(e.target) && setIsOpen(false);
    }
  };

  const changeSelect = (values) => {
    if (!!needPermissions && !hasPermission(needPermissions)) return;
    if (customSelectAllElement) {
      if (
        values.map(({value}) => value).includes(customSelectAllElement) &&
        !selectedValue.map(({value}) => value).includes(customSelectAllElement)
      )
        onChange([customSelectAllElement]);
      else
        onChange(
          values.map(({value}) => value).filter((item) => item !== customSelectAllElement)
        );
    } else realLabels ? onChange(values) : onChange(values.map(({value}) => value));
    noMultipleChange && setIsOpen(false);
  };

  const SelectionsRow = () => (
    <div
      className={cn("multiselect-custom_list __wrapper", {
        df: !!initialsSelectionMode,
      })}
    >
      <div
        className={cn("multiselect-custom_list __inner-wrapper", {
          "df-row-center": !!initialsSelectionMode,
        })}
      >
        {(maxSelectionsInRow
          ? selectedValue.slice(0, maxSelectionsInRow)
          : selectedValue
        ).map((item) =>
          initialsSelectionMode ? (
            <InitialsBadge
              key={item.value}
              firstName={list.find((val) => val.value === item.value)?.label}
              isCurrentUser={
                list.find((val) => val.value === item.value)?.value === currentUserId
              }
              needTooltip
              needClearClick
              onClick={() => handlerRemove(item.value)}
              addClass="mr-1"
            />
          ) : (
            <div
              key={item.value}
              className={cn(
                "multiselect-custom_item",
                {__small: small, __tiny: !!tiny},
                {[`__${color}`]: color}
              )}
            >
              <span className="mr-1">
                {realLabels
                  ? list.find((val) => val.value === item.value)?.label
                  : t(item.label)}
              </span>
              <ButtonDelete isWhite onClick={() => handlerRemove(item.value)} />
            </div>
          )
        )}
        {maxSelectionsInRow && selectedValue?.length > maxSelectionsInRow && (
          <CustomCounter
            count={selectedValue?.length - maxSelectionsInRow}
            bold={boldLabel}
            tiny={tiny}
            addClass="ml-0 mr-1"
          />
        )}
        {customSuffixSelectedRowcontent}
      </div>
    </div>
  );

  useEffect(() => {
    if (customSelectAllElement && value && value?.length === list?.length)
      setSelectedValue([{value: customSelectAllElement, label: customSelectAllElement}]);
    else
      realLabels
        ? setSelectedValue(toJS(value))
        : setSelectedValue(
            (toJS(value) && toJS(value).map((item) => ({value: item, label: item}))) || []
          );
  }, [value, customSelectAllElement, list?.length, realLabels]);

  useEffect(() => {
    document.body.addEventListener("click", backdropListener);
    return () => {
      document.body.removeEventListener("click", backdropListener);
    };
  });

  return (
    <div
      className={cn("multiselect-custom_wrapper", {
        __tiny: !!tiny,
        [addClass]: !!addClass,
      })}
    >
      {title && !titleInButton && (
        <div
          className={cn("multiselect-custom_title __wrapper", {
            __small: !!small,
            __tiny: !!tiny || !!tinyTitle,
            __black: !!blackTitle,
          })}
        >
          <p>{t(title.toUpperCase())}</p>
          {suffixTitleContent}
        </div>
      )}
      <div
        className={cn("multiselect-custom_inner-wrapper", {
          "__align-right": shouldAlignRight,
        })}
      >
        {leftIcon}
        {leftLabel && (
          <span
            className={cn("ws-nowrap df-row-start mt-1 mr-2", {
              __uppercase: !!uppercaseLabel,
              __bold: !!boldLabel,
              "fz-10": !!tiny || !!tinyLabel,
              "lh-26": !!tiny || !!tinyLabel,
            })}
          >
            {t(leftLabel)}
          </span>
        )}
        {!notShowingSelections && prefixSelections && <SelectionsRow />}
        <div
          className={cn("multiselect-custom_btn __wrapper", {
            "__align-right": shouldAlignRight,
          })}
        >
          <ButtonTransparent
            ref={addToFiltersRef}
            name={t(titleInButton ? title : "FIRST_SETUP_SELECT_ADD")}
            onClick={openSelect}
            icon={<IconPlus />}
            small={small}
            rounded
            tiny={tiny}
            uppercase={uppercaseButton}
            color={color}
            disabled={
              (noMultipleChange && selectedValue?.length > 0) ||
              disableMaxSelection ||
              disabled
            }
            disabledTooltip={disabledTooltip}
            addClass={cn("", {
              [addButtonSelectClass]: !!addButtonSelectClass,
              [multiSelectClassName]: !!multiSelectClassName,
              "bg-yellow": !!highlightIfEmpty,
            })}
          />
          <MultiSelect
            options={finalList}
            value={selectedValue}
            onChange={changeSelect}
            labelledBy={"Select"}
            hasSelectAll={!!selectAllElement}
            selectAllLabel={t(selectAllElement)}
            overrideStrings={{
              search: t(searchPlaceholder),
            }}
            className={cn(
              "multiselect-custom_select",
              {[multiSelectClassName]: !!multiSelectClassName},
              {"fucking-first-element": !!customSelectAllElement},
              {"with-search-row": !!needSearch},
              {"__align-right": shouldAlignRight}
            )}
            disableSearch={!needSearch}
            ClearIcon={<IconClose width={14} height={13} color={constColor.darkGray} />}
            ClearSelectedIcon={false}
            isOpen={isOpen}
            onMenuToggle={(value) => onMenuToggle?.(value)}
          />
        </div>
        {!notShowingSelections && !prefixSelections && <SelectionsRow />}
      </div>
    </div>
  );
};

export default MultipleSelect;
